import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Input,
  message,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType, TableRowSelection } from 'antd/es/table/interface';

import type {
  CafeExtractedShop,
  PartnerShopExtractedShopMap,
  PartnerShopExtractedShopRelationType,
} from '../PartnerCreatePage';
import { searchCafeExtractedShops } from '@/api/partnerShops';

const { Search } = Input;
const { Text, Title } = Typography;

interface ExtractedShopSelectStepProps {
  partnerShopExtractedShopMaps: PartnerShopExtractedShopMap[];
  onChangePartnerShopExtractedShopMaps: (
    nextPartnerShopExtractedShopMaps: PartnerShopExtractedShopMap[]
  ) => void;

  cafeExtractedShops: CafeExtractedShop[];
  onChangeCafeExtractedShops: (nextCafeExtractedShops: CafeExtractedShop[]) => void;
}

const SHOP_CATEGORY_OPTIONS = [
  { label: '전체', value: '' },
  { label: '예복', value: '예복' },
  { label: '드레스', value: '드레스' },
  { label: '스튜디오', value: '스튜디오' },
  { label: '메이크업', value: '메이크업' },
  { label: '예물', value: '예물' },
  { label: '한복', value: '한복' },
  { label: '베뉴', value: '베뉴' },
  { label: '혼수', value: '혼수' },
  { label: '기타', value: '기타' },
  { label: '알수없음', value: '알수없음' },
];

const RELATION_TYPE_OPTIONS: {
  label: string;
  value: PartnerShopExtractedShopRelationType;
}[] = [
  { label: '대표', value: 'PRIMARY' },
  { label: '별칭', value: 'ALIAS' },
  { label: '지점', value: 'BRANCH' },
  { label: '병합', value: 'MERGED' },
];

function mergeCafeExtractedShops(
  previousCafeExtractedShops: CafeExtractedShop[],
  nextCafeExtractedShops: CafeExtractedShop[]
) {
  const cafeExtractedShopMap = new Map<number, CafeExtractedShop>();

  previousCafeExtractedShops.forEach((item) => {
    cafeExtractedShopMap.set(Number(item.id), item);
  });

  nextCafeExtractedShops.forEach((item) => {
    cafeExtractedShopMap.set(Number(item.id), item);
  });

  return Array.from(cafeExtractedShopMap.values());
}

export default function ExtractedShopSelectStep({
  partnerShopExtractedShopMaps,
  onChangePartnerShopExtractedShopMaps,
  cafeExtractedShops,
  onChangeCafeExtractedShops,
}: ExtractedShopSelectStepProps) {
  const [keyword, setKeyword] = useState<string>('');
  const [shopCategory, setShopCategory] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const [rows, setRows] = useState<number>(10);

  const [loading, setLoading] = useState<boolean>(false);
  const [searchedCafeExtractedShops, setSearchedCafeExtractedShops] = useState<
    CafeExtractedShop[]
  >([]);
  const [total, setTotal] = useState<number>(0);
  const [checkedCafeExtractedShopIds, setCheckedCafeExtractedShopIds] = useState<
    React.Key[]
  >([]);
  const cafeExtractedShopsRef = useRef<CafeExtractedShop[]>(cafeExtractedShops);
  const requestSeqRef = useRef(0);

  useEffect(() => {
    cafeExtractedShopsRef.current = cafeExtractedShops;
  }, [cafeExtractedShops]);

  const partnerShopExtractedShopMapByCafeExtractedShopId = useMemo(() => {
    const map = new Map<number, PartnerShopExtractedShopMap>();

    partnerShopExtractedShopMaps.forEach((item) => {
      map.set(Number(item.cafeExtractedShopId), item);
    });

    return map;
  }, [partnerShopExtractedShopMaps]);

  const cafeExtractedShopById = useMemo(() => {
    const map = new Map<number, CafeExtractedShop>();

    cafeExtractedShops.forEach((item) => {
      map.set(Number(item.id), item);
    });

    searchedCafeExtractedShops.forEach((item) => {
      map.set(Number(item.id), item);
    });

    return map;
  }, [cafeExtractedShops, searchedCafeExtractedShops]);

  const selectedCafeExtractedShops = useMemo(() => {
    return partnerShopExtractedShopMaps
      .map((partnerShopExtractedShopMap) => {
        const cafeExtractedShop = cafeExtractedShopById.get(
          Number(partnerShopExtractedShopMap.cafeExtractedShopId)
        );

        if (!cafeExtractedShop) {
          return null;
        }

        return {
          ...cafeExtractedShop,
          relationType: partnerShopExtractedShopMap.relationType,
          delYn: partnerShopExtractedShopMap.delYn,
        };
      })
      .filter(Boolean) as Array<
      CafeExtractedShop & {
        relationType: PartnerShopExtractedShopRelationType;
        delYn: number;
      }
    >;
  }, [partnerShopExtractedShopMaps, cafeExtractedShopById]);

  const loadCafeExtractedShops = useCallback(async () => {
    const requestSeq = requestSeqRef.current + 1;
    requestSeqRef.current = requestSeq;
    setLoading(true);

    try {
      const result = await searchCafeExtractedShops({
        keyword,
        shopCategory,
        page,
        rows,
      });

      if (requestSeq !== requestSeqRef.current) {
        return;
      }

      const nextCafeExtractedShops = result.list || [];

      setSearchedCafeExtractedShops(nextCafeExtractedShops);
      setTotal(Number(result.total || 0));
      onChangeCafeExtractedShops(
        mergeCafeExtractedShops(cafeExtractedShopsRef.current, nextCafeExtractedShops)
      );
    } catch (error) {
      if (requestSeq !== requestSeqRef.current) {
        return;
      }

      console.error('[ExtractedShopSelectStep] failed to load cafeExtractedShops', error);

      const errorMessage =
        error instanceof Error
          ? error.message
          : 'AI 추출 업체 목록을 불러오지 못했습니다.';

      message.error(errorMessage);
    } finally {
      if (requestSeq === requestSeqRef.current) {
        setLoading(false);
      }
    }
  }, [keyword, shopCategory, page, rows, onChangeCafeExtractedShops]);

  useEffect(() => {
    loadCafeExtractedShops();
  }, [loadCafeExtractedShops]);

  const handleSearch = (nextKeyword: string) => {
    const trimmedKeyword = String(nextKeyword || '').trim();

    setKeyword(trimmedKeyword);
    setPage(1);
    setCheckedCafeExtractedShopIds([]);
  };

  const handleAddCheckedCafeExtractedShops = () => {
    const checkedCafeExtractedShops = searchedCafeExtractedShops.filter((item) =>
      checkedCafeExtractedShopIds.includes(item.id)
    );

    if (checkedCafeExtractedShops.length === 0) {
      message.warning('추가할 업체를 선택해 주세요.');
      return;
    }

    const nextPartnerShopExtractedShopMaps = [...partnerShopExtractedShopMaps];
    const nextCafeExtractedShopIdSet = new Set(
      nextPartnerShopExtractedShopMaps.map((item) =>
        Number(item.cafeExtractedShopId)
      )
    );

    checkedCafeExtractedShops.forEach((cafeExtractedShop) => {
      const cafeExtractedShopId = Number(cafeExtractedShop.id);

      if (nextCafeExtractedShopIdSet.has(cafeExtractedShopId)) {
        return;
      }

      nextPartnerShopExtractedShopMaps.push({
        cafeExtractedShopId,
        relationType:
          nextPartnerShopExtractedShopMaps.length === 0 ? 'PRIMARY' : 'ALIAS',
        delYn: 0,
      });

      nextCafeExtractedShopIdSet.add(cafeExtractedShopId);
    });

    onChangePartnerShopExtractedShopMaps(nextPartnerShopExtractedShopMaps);
    onChangeCafeExtractedShops(
      mergeCafeExtractedShops(cafeExtractedShops, checkedCafeExtractedShops)
    );
    setCheckedCafeExtractedShopIds([]);
  };

  const handleRemovePartnerShopExtractedShopMap = (cafeExtractedShopId: number) => {
    const nextPartnerShopExtractedShopMaps = partnerShopExtractedShopMaps.filter(
      (item) => Number(item.cafeExtractedShopId) !== Number(cafeExtractedShopId)
    );

    if (
      nextPartnerShopExtractedShopMaps.length > 0 &&
      !nextPartnerShopExtractedShopMaps.some(
        (item) => item.relationType === 'PRIMARY'
      )
    ) {
      nextPartnerShopExtractedShopMaps[0] = {
        ...nextPartnerShopExtractedShopMaps[0],
        relationType: 'PRIMARY',
      };
    }

    onChangePartnerShopExtractedShopMaps(nextPartnerShopExtractedShopMaps);
  };

  const handleChangePartnerShopExtractedShopMapRelationType = (
    cafeExtractedShopId: number,
    relationType: PartnerShopExtractedShopRelationType
  ) => {
    const nextPartnerShopExtractedShopMaps = partnerShopExtractedShopMaps.map(
      (item) => {
        if (relationType === 'PRIMARY') {
          return {
            ...item,
            relationType:
              Number(item.cafeExtractedShopId) === Number(cafeExtractedShopId)
                ? 'PRIMARY'
                : item.relationType === 'PRIMARY'
                  ? 'ALIAS'
                  : item.relationType,
          };
        }

        if (Number(item.cafeExtractedShopId) !== Number(cafeExtractedShopId)) {
          return item;
        }

        return {
          ...item,
          relationType,
        };
      }
    );

    onChangePartnerShopExtractedShopMaps(nextPartnerShopExtractedShopMaps);
  };

  const cafeExtractedShopColumns: ColumnsType<CafeExtractedShop> = [
    {
      title: '추출 업체명',
      dataIndex: 'shopName',
      width: 220,
      render: (text: string) => (
        <Space direction="vertical" size={2}>
          <Text strong>{text}</Text>
        </Space>
      ),
    },
    {
      title: '카테고리',
      dataIndex: 'shopCategory',
      width: 100,
      render: (shopCategoryValue: string | null) =>
        shopCategoryValue ? <Tag>{shopCategoryValue}</Tag> : '-',
    },
    {
      title: '후기 수',
      width: 140,
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <Text>{Number(row.articleCount || 0).toLocaleString()}개</Text>
          {/* <Text type="secondary" style={{ fontSize: 12 }}>
            직접 {Number(row.directReviewCount || 0).toLocaleString()} · 간접{' '}
            {Number(row.indirectReviewCount || 0).toLocaleString()}
          </Text> */}
        </Space>
      ),
    },
    {
      title: '이미지',
      dataIndex: 'imageCount',
      width: 90,
      render: (imageCount: number) =>
        `${Number(imageCount || 0).toLocaleString()}장`,
    },
    {
      title: '기존 연결',
      dataIndex: 'mappedPartnerShopText',
      width: 260,
      render: (mappedPartnerShopText?: string | null) => {
        if (!mappedPartnerShopText) {
          return <Text type="secondary">없음</Text>;
        }

        return (
          <Text type="warning" style={{ fontSize: 12 }}>
            {mappedPartnerShopText}
          </Text>
        );
      },
    },
  ];

  const selectedCafeExtractedShopColumns: ColumnsType<
    CafeExtractedShop & {
      relationType: PartnerShopExtractedShopRelationType;
      delYn: number;
    }
  > = [
    {
      title: '선택 업체',
      dataIndex: 'shopName',
      width: 260,
      render: (text: string) => (
        <Space direction="vertical" size={2}>
          <Text strong>{text}</Text>
        </Space>
      ),
    },
    {
      title: '관계',
      dataIndex: 'relationType',
      width: 140,
      render: (relationType: PartnerShopExtractedShopRelationType, row) => (
        <Select<PartnerShopExtractedShopRelationType>
          value={relationType}
          options={RELATION_TYPE_OPTIONS}
          style={{ width: 120 }}
          onChange={(nextRelationType) =>
            handleChangePartnerShopExtractedShopMapRelationType(
              row.id,
              nextRelationType
            )
          }
        />
      ),
    },
    {
      title: '근거',
      width: 190,
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <Text>
            후기 {Number(row.articleCount || 0).toLocaleString()} <br/> 이미지{' '}
            {Number(row.imageCount || 0).toLocaleString()}
          </Text>
          
        </Space>
      ),
    },
    {
      title: '삭제',
      width: 80,
      render: (_, row) => (
        <Button
          danger
          size="small"
          onClick={() => handleRemovePartnerShopExtractedShopMap(row.id)}
        >
          제거
        </Button>
      ),
    },
  ];

  const rowSelection: TableRowSelection<CafeExtractedShop> = {
    selectedRowKeys: checkedCafeExtractedShopIds,
    onChange: (nextSelectedRowKeys) =>
      setCheckedCafeExtractedShopIds(nextSelectedRowKeys),
    getCheckboxProps: (record) => ({
      disabled: partnerShopExtractedShopMapByCafeExtractedShopId.has(
        Number(record.id)
      ),
    }),
  };

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card>
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <div>
            <Title level={4} style={{ marginBottom: 4 }}>
              AI 추출 업체 연결
            </Title>
            <Text type="secondary">
              아래는 AI가 분석한 카페글에서 추출한 업체명입니다. 인증업체 작성시 후기와 사진이 연계됩니다
            </Text>
          </div>

          <Row gutter={12}>
            <Col xs={12} >
              <Space wrap style={{marginBottom: 12}}>
                <Search
                  allowClear
                  placeholder="업체명 검색 예: 헤리츠, 빌리언즈, 아틀레"
                  style={{ width: 160 }}
                  onSearch={handleSearch}
                />

                <Select
                  value={shopCategory}
                  options={SHOP_CATEGORY_OPTIONS}
                  style={{ width: 100 }}
                  onChange={(nextShopCategory) => {
                    setShopCategory(nextShopCategory);
                    setPage(1);
                  }}
                />

                {/* <InputNumber
                  min={5}
                  max={100}
                  value={rows}
                  onChange={(nextRows) => {
                    setRows(Number(nextRows || 10));
                    setPage(1);
                  }}
                  addonAfter="개"
                /> */}

                <Button onClick={() => loadCafeExtractedShops()}>새로고침</Button>

                <Button type="primary" onClick={handleAddCheckedCafeExtractedShops}>
                  선택 항목 추가
                </Button>
              </Space>

              <Table<CafeExtractedShop>
                rowKey="id"
                loading={loading}
                columns={cafeExtractedShopColumns}
                dataSource={searchedCafeExtractedShops}
                rowSelection={rowSelection}
                pagination={{
                  current: page,
                  pageSize: rows,
                  total,
                  showSizeChanger: true,
                  onChange: (nextPage, pageSize) => {setPage(nextPage); setRows(pageSize)},
                }}
                size="middle"
              />
            </Col>
            <Col xs={12} >
              <Space direction="vertical" size={12} style={{ width: '100%' }}>
                <div>
                  <Title level={4} style={{ marginBottom: 4 }}>
                    선택된 업체
                  </Title>
                  <Text type="secondary">
                    
                  </Text>
                </div>

                <Table
                  rowKey="id"
                  columns={selectedCafeExtractedShopColumns}
                  dataSource={selectedCafeExtractedShops}
                  pagination={false}
                  locale={{
                    emptyText: '아직 연결된 업체가 없습니다.',
                  }}
                />
              </Space>
            </Col>
          </Row>
          
        </Space>
      </Card>

      <Card>
        
      </Card>
    </Space>
  );
}
